import type { Room } from "@prisma/client";
import type { TranslationFunctions } from "~/i18n/i18n-types";
import { STTDomain, STTProvider, supportsDomain } from "./signalr-types";

/**
 * Sentinel used by `<Select>` for "no provider override". Radix selects cannot
 * hold a `null` value, so the platform default is represented as a string.
 */
export const DEFAULT_MODEL_VALUE = "default";

export interface SttModelOption {
	/** Stable `<Select>` value: the numeric provider, or `DEFAULT_MODEL_VALUE`. */
	value: string;
	/** `null` = platform default (MSFT diarization), no provider override. */
	provider: STTProvider | null;
	label: string;
	description: string;
	shortDescription: string;
	/** Optional emphasis tag. Rendered in settings, omitted during room creation. */
	badge?: string;
	/** Whether this model accepts an `STTDomain` (e.g. Math Optimized). */
	supportsDomain: boolean;
}

/**
 * Single source of truth for the selectable transcription models, shared by the
 * in-room settings selector and the room-creation flow so the two can never
 * drift apart.
 */
export const getSttModelOptions = (
	LL: TranslationFunctions
): SttModelOption[] => [
	{
		value: DEFAULT_MODEL_VALUE,
		provider: null,
		label: LL.TRANSCRIPTION_SETTINGS.MODEL_DEFAULT(),
		description: LL.TRANSCRIPTION_SETTINGS.MODEL_DEFAULT_DESC(),
		shortDescription: LL.TRANSCRIPTION_SETTINGS.MODEL_DEFAULT_SHORT_DESC(),
		supportsDomain: false,
	},
	{
		value: String(STTProvider.OAI_Whisper),
		provider: STTProvider.OAI_Whisper,
		label: LL.TRANSCRIPTION_SETTINGS.MODEL_WHISPER(),
		description: LL.TRANSCRIPTION_SETTINGS.MODEL_WHISPER_DESC(),
		shortDescription: LL.TRANSCRIPTION_SETTINGS.MODEL_WHISPER_SHORT_DESC(),
		badge: LL.TRANSCRIPTION_SETTINGS.MODEL_ENHANCED(),
		supportsDomain: supportsDomain(STTProvider.OAI_Whisper),
	},
	{
		value: String(STTProvider.OAI_GPT_4o_Transcribe),
		provider: STTProvider.OAI_GPT_4o_Transcribe,
		label: LL.TRANSCRIPTION_SETTINGS.MODEL_GPT4O(),
		description: LL.TRANSCRIPTION_SETTINGS.MODEL_GPT4O_DESC(),
		shortDescription: LL.TRANSCRIPTION_SETTINGS.MODEL_GPT4O_SHORT_DESC(),
		badge: LL.TRANSCRIPTION_SETTINGS.MODEL_PREMIUM(),
		supportsDomain: supportsDomain(STTProvider.OAI_GPT_4o_Transcribe),
	},
	{
		value: String(STTProvider.OAI_GPT_4oMini_Transcribe),
		provider: STTProvider.OAI_GPT_4oMini_Transcribe,
		label: LL.TRANSCRIPTION_SETTINGS.MODEL_GPT4O_MINI(),
		description: LL.TRANSCRIPTION_SETTINGS.MODEL_GPT4O_MINI_DESC(),
		shortDescription:
			LL.TRANSCRIPTION_SETTINGS.MODEL_GPT4O_MINI_SHORT_DESC(),
		supportsDomain: supportsDomain(STTProvider.OAI_GPT_4oMini_Transcribe),
	},
	{
		value: String(STTProvider.AZAI_Fast_Transcription),
		provider: STTProvider.AZAI_Fast_Transcription,
		label: LL.TRANSCRIPTION_SETTINGS.MODEL_FAST(),
		description: LL.TRANSCRIPTION_SETTINGS.MODEL_FAST_DESC(),
		shortDescription: LL.TRANSCRIPTION_SETTINGS.MODEL_FAST_SHORT_DESC(),
		supportsDomain: supportsDomain(STTProvider.AZAI_Fast_Transcription),
	},
];

/** Convert a `<Select>` value back into a provider. */
export const toSttProvider = (value: string): STTProvider | null =>
	value === DEFAULT_MODEL_VALUE ? null : (Number(value) as STTProvider);

/** Convert a provider into its stable `<Select>` value. */
export const toSttModelValue = (provider: STTProvider | null): string =>
	provider === null ? DEFAULT_MODEL_VALUE : String(provider);

/** Look up an option by `<Select>` value, falling back to the platform default. */
export const findSttModel = (
	options: SttModelOption[],
	value: string
): SttModelOption | undefined =>
	options.find((option) => option.value === value) ?? options[0];

/**
 * Collapse a provider/domain pair into a coherent one. A domain is only
 * meaningful for providers that accept it, so anything else resolves to `None`.
 * Applied before persisting a room and before sending a transcription request.
 */
export const resolveSttDomain = (
	provider: STTProvider | null,
	domain: STTDomain
): STTDomain =>
	provider !== null && supportsDomain(provider) ? domain : STTDomain.None;

const numericEnumValues = (enumObject: Record<string, string | number>) =>
	new Set(
		Object.values(enumObject).filter(
			(value): value is number => typeof value === "number"
		)
	);

const KNOWN_PROVIDERS = numericEnumValues(STTProvider);
const KNOWN_DOMAINS = numericEnumValues(STTDomain);

/** The room's transcription policy, narrowed from its persisted columns. */
export interface RoomModelSelection {
	provider: STTProvider | null;
	domain: STTDomain;
	/** Participants (host included) cannot override the room's model. */
	locked: boolean;
}

/** The `Room` columns that carry the host's transcription choices. */
export type RoomModelColumns = Pick<
	Room,
	"sttProvider" | "sttDomain" | "modelLocked"
>;

/**
 * Narrow a room's raw integer columns into a usable selection. Values that no
 * longer map to a known enum member (e.g. a provider retired after the room was
 * created) degrade to the platform default instead of reaching the STT service.
 */
export const toRoomModelSelection = (
	room: RoomModelColumns
): RoomModelSelection => {
	const provider =
		room.sttProvider !== null && KNOWN_PROVIDERS.has(room.sttProvider)
			? (room.sttProvider as STTProvider)
			: null;
	const domain = KNOWN_DOMAINS.has(room.sttDomain)
		? (room.sttDomain as STTDomain)
		: STTDomain.None;

	return {
		provider,
		domain: resolveSttDomain(provider, domain),
		locked: room.modelLocked,
	};
};
